/**
 * What the customer picked, on the job's Overview (ADR 0304).
 *
 * Lysa asked which colour a customer had chosen, and the answer was in the
 * handoff email and nowhere she could see it. The colour is settled in three
 * places at three different times — the estimate, the handoff email, the
 * material order — and a person can also just say it. This decides which one
 * the Overview shows, and says where it came from.
 *
 * The material order wins over the email because it is what is actually being
 * bought: production builds it from the email, so when the two differ the
 * order is the later word, and the difference is shown rather than hidden.
 */

import {
  COLOR_MATCH,
  colorOptionsFor,
  colorRuleFor,
  type ColorProduct,
} from "./colorRules.js";

/** Where the colour on the Overview was read from. */
export type ColourSource = "chosen" | "order" | "sends" | "estimate";

export const COLOUR_SOURCE_LABEL: Record<ColourSource, string> = {
  chosen: "chosen by hand",
  order: "from the material order",
  sends: "from the Sends email",
  estimate: "from the estimate",
};

/** One line that carries a colour: a material order line or an estimate line. */
export interface ColouredLine {
  name: string;
  color?: string | null;
  /** The catalogue's rule for the line's product: `required` is the roof's own colour. */
  rule?: string | null;
}

export interface JobColour {
  colour: string;
  source: ColourSource;
  /**
   * What the email said, when the material order says something else. Shown
   * beside the colour, because one of them is wrong and somebody has to ask.
   */
  sendsSays: string | null;
}

/** The lines the generator writes when it has nothing to fill in. */
const PLACEHOLDER = /^(not signed in the app|no colou?rs on the estimate)/i;

/**
 * The bullets under SELECTIONS in a handoff email, without their bullets.
 *
 * Read from the text as it went, so a colour the rep typed in by hand counts
 * the same as one the app filled in. Stops at the first blank line or the next
 * heading. The generator's own "type it here" placeholders are left out: they
 * are instructions to the rep, not something the customer picked.
 */
export function sendsSelections(body: string | null | undefined): string[] {
  const lines = (body ?? "").split(/\r?\n/).map((line) => line.trim());
  const start = lines.findIndex((line) => /^selections:?$/i.test(line));
  if (start === -1) return [];
  const out: string[] = [];
  for (const line of lines.slice(start + 1)) {
    if (line === "" || /^[A-Z][A-Z ]+:?$/.test(line)) break;
    const text = line.replace(/^[•\-*–]\s*/, "").trim();
    if (text && !PLACEHOLDER.test(text)) out.push(text);
  }
  return out;
}

/** A selection's label and value: "Shingle color: Weathered Wood", "HDZ — Charcoal". */
function splitSelection(line: string): { label: string; value: string } {
  const colon = line.indexOf(":");
  if (colon !== -1) {
    return {
      label: line.slice(0, colon).trim(),
      value: line.slice(colon + 1).trim(),
    };
  }
  const parts = line.split(/\s+[—–-]\s+/);
  return parts.length < 2
    ? { label: line.trim(), value: "" }
    : { label: (parts[0] ?? "").trim(), value: (parts.at(-1) ?? "").trim() };
}

const isColourMatch = (value: string) =>
  value.toLowerCase() === COLOR_MATCH.toLowerCase();

/**
 * Which of the selections is the roof's colour, and the colour it names.
 *
 * A line whose label says "colour" or "color" wins — "Shingle color: Weathered
 * Wood" — and otherwise the first of the generator's own "GAF Timberline HDZ —
 * Weathered Wood", the generator writing the roof's estimate, and the roof's
 * own lines within it, before anything else (a gutters estimate signed first
 * still comes after it). "Color
 * Match" is never the colour: the generator writes it for a ridge cap that
 * follows the roof, a note rather than a choice. Upgrades and things still
 * being decided are not colours either.
 */
function colourLine(
  selections: readonly string[],
): { index: number; colour: string } | null {
  const parts = selections.map(splitSelection);
  const usable = (i: number) => {
    const value = parts[i]?.value ?? "";
    return value !== "" && !isColourMatch(value);
  };
  for (let i = 0; i < selections.length; i += 1) {
    if (usable(i) && /colou?r/i.test(parts[i]?.label ?? "")) {
      return { index: i, colour: parts[i]?.value ?? "" };
    }
  }
  for (let i = 0; i < selections.length; i += 1) {
    const line = selections[i] ?? "";
    if (/^(upgrades|still deciding)\b/i.test(line)) continue;
    const last = (
      line
        .split(/\s+[—–]\s+/)
        .slice(1)
        .at(-1) ?? ""
    ).trim();
    if (last && !isColourMatch(last)) return { index: i, colour: last };
  }
  return null;
}

/** The colour a list of selections names, if it names one. */
export function colourFromSelections(
  selections: readonly string[],
): string | null {
  return colourLine(selections)?.colour ?? null;
}

/** A line's colour, unless it has none or only follows the roof. */
export function realColour(line: Pick<ColouredLine, "color">): string | null {
  const colour = (line.color ?? "").trim();
  return colour && colour !== COLOR_MATCH ? colour : null;
}

/**
 * The roof's colour from a list of lines: the first colour on a line whose
 * product must have one chosen. Only when no line is such a product does any
 * other real colour count, so a brown drip edge is never read as the roof's
 * colour just because the shingle has none yet. "Color Match" is not a
 * choice, only a note that it follows the roof. The Sends questions start on
 * this too (ADR 0303), so they and the Overview agree.
 */
export function colourOf(lines: readonly ColouredLine[]): string | null {
  const required = lines.filter((line) => line.rule === "required");
  for (const line of required.length ? required : lines) {
    if (realColour(line)) return realColour(line);
  }
  return null;
}

/**
 * The colours a job's roof can be, for a dropdown: every choice of every
 * product on it whose colour must be chosen.
 */
export function jobColourOptions(products: readonly ColorProduct[]): string[] {
  return [
    ...new Set(
      products
        .filter((product) => colorRuleFor(product) === "required")
        .flatMap((product) => colorOptionsFor(product)),
    ),
  ];
}

const same = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The colour the Overview shows, and where it came from.
 *
 * A colour somebody chose by hand wins outright; that is what the dropdown is
 * for. Then the material order, then the Sends email, then the estimate.
 * Null when none of them says.
 */
export function jobColour(input: {
  chosen?: string | null;
  orderLines?: readonly ColouredLine[];
  sendsEmail?: string | null;
  estimateLines?: readonly ColouredLine[];
}): JobColour | null {
  const sends = colourFromSelections(sendsSelections(input.sendsEmail));
  const order = colourOf(input.orderLines ?? []);
  const chosen = (input.chosen ?? "").trim();
  if (chosen) return { colour: chosen, source: "chosen", sendsSays: null };
  if (order) {
    return {
      colour: order,
      source: "order",
      sendsSays: sends && !same(sends, order) ? sends : null,
    };
  }
  if (sends) return { colour: sends, source: "sends", sendsSays: null };
  const estimate = colourOf(input.estimateLines ?? []);
  return estimate
    ? { colour: estimate, source: "estimate", sendsSays: null }
    : null;
}

/**
 * Every other colour on the material order, as "Drip Edge — Brown": the
 * second things production has to get right. The roof's own colour and
 * "Color Match" lines are left out, the first being shown already and the
 * second not being a choice.
 */
export function otherOrderColours(
  lines: readonly ColouredLine[],
  roof: string | null,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines) {
    const colour = realColour(line);
    if (!colour || (roof && same(colour, roof) && line.rule === "required"))
      continue;
    const name = (line.name.split("|").at(-1) ?? "").trim() || line.name.trim();
    const text = `${name} — ${colour}`;
    if (seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    out.push(text);
  }
  return out;
}

/** Everything the Overview's Selections card shows (ADR 0304). */
export interface SelectionsSummary {
  /** The colour shown: the one chosen by hand, when there is one. */
  colour: JobColour | null;
  /** What the job itself says, leaving the hand-picked colour aside. */
  fromJob: JobColour | null;
  /** The rest of the Sends email's SELECTIONS, its colour line left out. */
  picked: string[];
  /** The material order's other colours, as "Drip Edge — Brown". */
  others: string[];
}

/**
 * What the Selections card shows. The colour is `jobColour`'s, given the
 * hand-picked one, so the rule that it wins lives in one place. The Sends
 * email's colour line is left out of the list because the colour shows it
 * already; every other bullet stays, a "Color Match" line among them.
 */
export function selectionsSummary(input: {
  chosen?: string | null;
  orderLines?: readonly ColouredLine[];
  sendsEmail?: string | null;
  estimateLines?: readonly ColouredLine[];
}): SelectionsSummary {
  const colour = jobColour(input);
  const fromJob = jobColour({ ...input, chosen: null });
  const selections = sendsSelections(input.sendsEmail);
  const line = colourLine(selections);
  return {
    colour,
    fromJob,
    picked: selections.filter((_, i) => i !== line?.index),
    others: otherOrderColours(input.orderLines ?? [], colour?.colour ?? null),
  };
}
